import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import UnsavedChangesModal from "@/shared/components/ui/UnsavedChangesModal";

interface UnsavedChangesConfirmOptions {
  message?: string;
}

interface UnsavedChangesContextValue {
  confirmUnsavedChanges: (options?: UnsavedChangesConfirmOptions) => Promise<boolean>;
  dismissUnsavedChanges: () => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);

  const resolveAndClose = useCallback((confirmed: boolean) => {
    resolverRef.current?.(confirmed);
    resolverRef.current = null;
    setIsOpen(false);
    setMessage(undefined);
  }, []);

  const dismissUnsavedChanges = useCallback(() => {
    if (!resolverRef.current) {
      return;
    }

    resolveAndClose(false);
  }, [resolveAndClose]);

  const confirmUnsavedChanges = useCallback((options?: UnsavedChangesConfirmOptions) => {
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }

    setMessage(options?.message);
    setIsOpen(true);

    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const value = useMemo(
    () => ({
      confirmUnsavedChanges,
      dismissUnsavedChanges,
    }),
    [confirmUnsavedChanges, dismissUnsavedChanges],
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <UnsavedChangesModal
        isOpen={isOpen}
        onCancelStay={() => resolveAndClose(false)}
        onConfirmLeave={() => resolveAndClose(true)}
        {...(message ? { message } : {})}
      />
    </UnsavedChangesContext.Provider>
  );
};

export const useUnsavedChangesConfirmation = (): UnsavedChangesContextValue => {
  const context = useContext(UnsavedChangesContext);

  if (!context) {
    throw new Error("useUnsavedChangesConfirmation must be used within an UnsavedChangesProvider");
  }

  return context;
};
